// src/context/ThemeContext.js
import React, { createContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

// Function to convert hex to RGB
function hexToRgb(hex) {
  // Remove the leading '#' if present
  const normalizedHex = hex.replace(/^#/, '');
  // Parse the string into R, G, B values
  const bigint = parseInt(normalizedHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}

// Function to convert RGB back to hex
function rgbToHex(r, g, b) {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Function to mix colors based on light or dark mode
function mixColor(color, mode, grade = 0.1) {
  const [r, g, b] = hexToRgb(color);
  
  // Adjust the RGB values based on the mode and grade
  const factor = mode === 'dark' ? -grade : grade;
  
  const mixValue = (value) => {
    return Math.min(255, Math.max(0, value + value * factor));
  };

  const newR = mixValue(r);
  const newG = mixValue(g);
  const newB = mixValue(b);

  return rgbToHex(Math.round(newR), Math.round(newG), Math.round(newB));
}

const predefinedThemes = {
  light: {
    mode: 'light',
    mainBody: '#FFFFFF',
    mainField: '#F0F0F0',
    mainText: '#000000',
    mainAccent: '#007BFF',
    mainOff: '#0056B3'
  },
  dark: {
    mode: 'dark',
    mainBody: '#1D1E33',
    mainField: '#283655',
    mainText: '#D4F1F4',
    mainAccent: '#4B778D',
    mainOff: '#F7D6E0'
  },
  cool: {
    mode: 'dark',
    mainBody: '#0F1D29',
    mainField: '#407269',
    mainText: '#FFFF96',
    mainAccent: '#9C817E',
    mainOff: '#4D3D36',
  },
  me: {
    mode: 'dark',
    mainBody: '#50535C',
    mainField: '#131D2B',
    mainText: '#ABCCEE',
    mainAccent: '#D09F8D'
  }
}
/*
const defaultTheme = {
  mode: 'dark', // light or dark
  accent: '#6E2D88', // default button color
  offAccent: '#9261A5', // Default offAccent color
  textCombo: { field: '#ffffff', color: '#000000' }, // text field and text color
  contactBackgroundColor: '#0F1D29',
  contactTextColor: '#FFFF96',
  inputBorderColor: '#0F1D29',
  inputBackgroundColor: '#407269',
  inputTextColor: '#FFFF96',
  inputFocusBorderColor: '#FFFF96',
  inputDisabledBackgroundColor: '#9C817E',
  inputDisabledTextColor: '#4D3D36',
  inputHoverBorderColor: '#500F6B',
  mainBody: '0F1D29',
  mainField: '407269',
  mainText: 'FFFF96',
  mainAccent: '9C817E',
  mainOff: '4D3D36'
};
*/

// Create Theme Context
const defaultTheme = predefinedThemes.dark;

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Set the initial theme state and calculate offAccent using mixColor
    const initialTheme = { ...defaultTheme, offAccent: mixColor(defaultTheme.mainAccent, defaultTheme.mode) };
    const savedTheme = Cookies.get('theme');
    return savedTheme ? JSON.parse(savedTheme) : initialTheme;
  });

  // Load saved preferences from cookies when component mounts
  useEffect(() => {
    const savedTheme = Cookies.get('theme');
    if (savedTheme) {
      try {
        const parsedTheme = JSON.parse(savedTheme);
        setTheme(parsedTheme);
      } catch (error) {
        console.error('Error parsing theme from cookie:', error);
      }
    }
  }, []);

  // Update cookies whenever the theme changes
  useEffect(() => {
    Cookies.set('theme', JSON.stringify(theme), { expires: 365 });
  }, [theme]);

  const updateTheme = (newTheme) => {
    // Calculate offAccent dynamically whenever the theme changes
    const updatedTheme = {
      ...theme,
      ...newTheme,
      mainOff: mixColor(newTheme.mainAccent || theme.mainAccent, newTheme.mode || theme.mode),
    };
    setTheme(updatedTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, predefinedThemes }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
